import { useEffect, useRef, useState } from 'react';
import { fetchIndex, type IndexJson } from '../api/statusApi';

const POLL_INTERVAL_MS = 60_000; // matches the Flutter StatusBanner's cadence (CONTRACTS.md §3)
const STALE_AFTER_MS = 3 * POLL_INTERVAL_MS; // no fresh fetch in 3 windows → surface as stale

export interface UseStatusPollingResult {
  /** Last successfully fetched payload. Never cleared on a transient failure (fail-quiet). */
  data: IndexJson | null;
  /** Timestamp (client clock) of the last successful fetch, or null before the first one lands. */
  lastUpdatedAt: Date | null;
  /** True once we have data but haven't refreshed it recently — fetches are failing silently. */
  isStale: boolean;
  /** True until the very first fetch attempt resolves (success or failure). */
  isLoading: boolean;
}

/**
 * Polls same-origin /index.json every 60s. Fails quiet: a transient network error
 * or bad response never clears already-loaded data or throws — it just leaves the
 * last-known-good snapshot in place and lets `isStale` reflect how old it is.
 */
export function useStatusPolling(): UseStatusPollingResult {
  const [data, setData] = useState<IndexJson | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const lastUpdatedAtRef = useRef<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    let staleTimer: ReturnType<typeof setInterval> | undefined;

    async function poll() {
      try {
        const next = await fetchIndex();
        if (cancelled) return;
        const now = new Date();
        setData(next);
        setLastUpdatedAt(now);
        lastUpdatedAtRef.current = now;
        setIsStale(false);
      } catch (err) {
        // Fail-quiet: log for diagnostics, keep showing last-known-good data.
        console.warn('bloomz-status: /index.json fetch failed, keeping last-known-good data', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void poll();
    const intervalId = setInterval(() => void poll(), POLL_INTERVAL_MS);

    // Independently recompute staleness so a hung fetch still eventually flags itself.
    staleTimer = setInterval(() => {
      const last = lastUpdatedAtRef.current;
      if (last && Date.now() - last.getTime() > STALE_AFTER_MS) {
        setIsStale(true);
      }
    }, 15_000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      if (staleTimer) clearInterval(staleTimer);
    };
  }, []);

  return { data, lastUpdatedAt, isStale, isLoading };
}
